import HttpService from './http-service';
import EdudoResponse from '../models/edudo-response';

export default class AnnouncementService {
  private schoolUuid: string;

  constructor(schoolUuid: string) {
    this.schoolUuid = schoolUuid;
  }

  private basePath(): string {
    return `/v1/schools/${this.schoolUuid}/announcements`;
  }

  // `school-backoffice-bff` is a top-level path prefix, not nested under `v1/schools/...` —
  // it's a distinct edge surface composing over (not replacing) the canonical routes above.
  private backofficeBasePath(): string {
    return `/school-backoffice-bff/v1/schools/${this.schoolUuid}/announcements`;
  }

  async listBackoffice(
    limit: number = 20,
    offset: number = 0
  ): Promise<BackofficeAnnouncementListItemDto[]> {
    const response = await HttpService.getJson<
      EdudoResponse<BackofficeAnnouncementListItemDto[]>
    >(`${this.backofficeBasePath()}?limit=${limit}&offset=${offset}`);

    return response.data;
  }

  // O BFF limita `limit` a 100, então a lista completa vem página por página.
  async listAllBackoffice(): Promise<BackofficeAnnouncementListItemDto[]> {
    const pageSize = 100;
    const all: BackofficeAnnouncementListItemDto[] = [];

    for (let offset = 0; ; offset += pageSize) {
      const page = await this.listBackoffice(pageSize, offset);
      all.push(...page);
      if (page.length < pageSize) return all;
    }
  }

  async getBackoffice(announcementId: string): Promise<BackofficeAnnouncementDetailDto> {
    const response = await HttpService.getJson<EdudoResponse<BackofficeAnnouncementDetailDto>>(
      `${this.backofficeBasePath()}/${announcementId}`
    );

    return response.data;
  }

  async listAnnouncements(
    limit: number = 20,
    offset: number = 0
  ): Promise<AnnouncementSummaryDto[]> {
    const response = await HttpService.getJson<EdudoResponse<AnnouncementSummaryDto[]>>(
      `${this.basePath()}?limit=${limit}&offset=${offset}`
    );

    return response.data;
  }

  async getAnnouncement(announcementId: string): Promise<AnnouncementDto> {
    const response = await HttpService.getJson<EdudoResponse<AnnouncementDto>>(
      `${this.basePath()}/${announcementId}`
    );

    return response.data;
  }

  async createAnnouncement(data: AnnouncementRequest): Promise<AnnouncementDto> {
    const response = await HttpService.postJson<EdudoResponse<AnnouncementDto>>(
      this.basePath(),
      data
    );

    return response.data;
  }

  async updateAnnouncement(
    announcementId: string,
    data: AnnouncementRequest
  ): Promise<AnnouncementDto> {
    const response = await HttpService.putJson<EdudoResponse<AnnouncementDto>>(
      `${this.basePath()}/${announcementId}`,
      data
    );

    return response.data;
  }

  async deleteAnnouncement(announcementId: string): Promise<void> {
    await HttpService.delete(`${this.basePath()}/${announcementId}`);
  }
}
